/**
 * WS-GAPS2 B-W11 — the schedule conflicts a person is notified about, on a page they can open. READ-ONLY.
 *
 * LEDGER W4d-3: approvers AND operational dispatchers are notified of reserved-window schedule conflicts; "notifying
 * a dispatcher grants no approval authority (D-1b still applies)". Before this page the conflict list
 * (ScheduleConflictPanel) and the notification bell (NotificationPanel) existed only on the Dayboard `/`, which a
 * dispatcher-only member cannot open — they were notified of something they could not see.
 *
 * What it shows: the server's conflict list (`list_reserved_window_conflicts`, audience `is_schedule_conflict_viewer`)
 * and the conflict notifications the person receives (the notifications read policy already admits a dispatcher to
 * exactly these causes). What it does NOT do: decide a rollover, assign, move, place or write anything. Decisions stay
 * in RolloverDecisionPanel on the Dayboard, approver-only.
 *
 * Route: `/schedule-conflicts`, behind ScheduleConflictViewerRoute (the server predicate); `/` sends a member it
 * refuses here, and this page's own gate answers 403 to anyone the server does not admit.
 */
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { PageWithTopBar } from '@/components/layout/PageWithTopBar';
import { describeConflictRow, isKeptProtectedCommitment, type ReservedWindowConflictRow } from '@/components/scheduling/ScheduleConflictPanel';

export const SCHEDULE_CONFLICTS_PATH = '/schedule-conflicts';

/** The notification causes this page is about (the W4e dispatcher notification policy admits exactly these). */
export const SCHEDULE_CONFLICT_NOTIFICATION_CAUSES = ['reserved_window_overlap', 'reserved_window_overlap_needs_person'] as const;

export interface ScheduleConflictNotification {
  id: string;
  title: string;
  message: string;
  created_at: string;
  data: { kind?: string; cause?: string; ticket_id?: string } | null;
}

export function isScheduleConflictNotification(n: Pick<ScheduleConflictNotification, 'data'>): boolean {
  return n.data?.kind === 'rollover_needs_placement'
    && (SCHEDULE_CONFLICT_NOTIFICATION_CAUSES as readonly string[]).includes(String(n.data?.cause ?? ''));
}

type LooseRpc = { rpc: (f: string, a: Record<string, unknown>) => PromiseLike<{ data: unknown; error: unknown }> };

export default function ScheduleConflicts() {
  const { activeShop } = useAuth();
  const shopId = activeShop?.id ?? null;
  const [rows, setRows] = useState<ReservedWindowConflictRow[] | null>(null);
  const [notes, setNotes] = useState<ScheduleConflictNotification[]>([]);

  const load = useCallback(async () => {
    if (!shopId) return;
    try {
      const { data, error } = await (supabase as unknown as LooseRpc).rpc('list_reserved_window_conflicts', { p_shop: shopId });
      if (!error) setRows(Array.isArray(data) ? (data as ReservedWindowConflictRow[]) : []);
      else if ((error as { code?: string }).code === '42501') setRows([]);
    } catch { /* a failed READ keeps the last list */ }
    try {
      const { data } = await supabase
        .from('notifications')
        .select('id, title, message, created_at, data')
        .eq('shop_id', shopId)
        .order('created_at', { ascending: false })
        .limit(100);
      setNotes(((data ?? []) as unknown as ScheduleConflictNotification[]).filter(isScheduleConflictNotification));
    } catch { /* keep the last list */ }
  }, [shopId]);

  useEffect(() => {
    if (!shopId) return;
    void load();
    const t = setInterval(() => { void load(); }, 60_000);
    return () => clearInterval(t);
  }, [shopId, load]);

  return (
    <PageWithTopBar>
      <div className="mx-auto w-full max-w-3xl space-y-6 px-4 py-6" data-testid="schedule-conflicts-page">
        <header className="space-y-1">
          <h1 className="text-2xl font-semibold">Schedule conflicts</h1>
          <p className="text-sm text-muted-foreground">
            Read-only. Work whose time overlaps an approved rollover&apos;s reserved time, and work waiting to be placed
            because of it. Rollover decisions stay with the shop&apos;s approvers on the Dayboard.
          </p>
        </header>

        <section aria-label="Current conflicts" className="space-y-2">
          <h2 className="text-lg font-medium">Current conflicts</h2>
          {rows === null ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="schedule-conflicts-empty">No schedule conflicts right now.</p>
          ) : (
            <ul className="space-y-2">
              {rows.map((r) => {
                const p = describeConflictRow(r);
                return (
                  <li key={r.ticket_id} data-testid="schedule-conflicts-page-row" data-state={r.state}
                    data-kind={isKeptProtectedCommitment(r) ? 'protected_commitment_kept' : 'reserved_window_overlap'}
                    className="rounded-md border border-red-400 bg-red-50 p-3 text-sm text-red-950 dark:bg-red-950 dark:text-red-50">
                    <div className="font-medium">
                      {r.simple_ticket_number ? `#${r.simple_ticket_number}` : r.ticket_id}
                      {r.scheduled_date ? ` · ${r.scheduled_date}` : ''}
                    </div>
                    <div>{p.label}</div>
                    <div className="opacity-80">{p.detail}</div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section aria-label="Conflict notifications" className="space-y-2">
          <h2 className="text-lg font-medium">Notifications</h2>
          {notes.length === 0 ? (
            <p className="text-sm text-muted-foreground">No schedule conflict notifications.</p>
          ) : (
            <ul className="space-y-2">
              {notes.map((n) => (
                <li key={n.id} data-testid="schedule-conflict-notification" className="rounded-md border p-3 text-sm">
                  <div className="font-medium">{n.title}</div>
                  <div className="text-muted-foreground">{n.message}</div>
                  <div className="text-xs text-muted-foreground">{new Date(n.created_at).toLocaleString()}</div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </PageWithTopBar>
  );
}
